import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackUserParams, trackEvent, GOALS } from "@/lib/analytics";

interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  address: string | null;
  total_orders_amount: number;
  discount_level: number;
  is_partner: boolean;
  referral_code: string | null;
  // New loyalty fields
  loyalty_status: string;
  current_discount_percent: number;
  next_tier_threshold: number;
  amount_to_next_tier: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, marketingConsent?: boolean) => Promise<{ error: any; data: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (authUser: User) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authUser.id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching profile:", error);
      return;
    }

    if (data) {
      setProfile(data as Profile);
      return;
    }

    // If profile row is missing (e.g. trigger not configured), create it on first sign-in.
    const fullName =
      typeof authUser.user_metadata?.full_name === "string"
        ? (authUser.user_metadata.full_name as string)
        : null;
    const marketingConsent =
      typeof authUser.user_metadata?.marketing_consent === "boolean"
        ? (authUser.user_metadata.marketing_consent as boolean)
        : null;

    const { data: upserted, error: upsertError } = await supabase
      .from("profiles")
      .upsert(
        {
          id: authUser.id,
          email: authUser.email ?? null,
          full_name: fullName,
          marketing_consent: marketingConsent,
        },
        { onConflict: "id" }
      )
      .select("*")
      .single();

    if (upsertError) {
      console.error("Error creating profile:", upsertError);
      return;
    }

    setProfile(upserted as Profile);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user);
          }, 0);

          // Send userParams on every login for Metrika segmentation
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            const u = session.user;
            const regSource = (u.user_metadata?.registration_source as string) || 'unknown';
            trackUserParams({
              user_id: u.id,
              registered: true,
              email_confirmed: !!u.email_confirmed_at,
              registration_source: regSource,
            });

            // Fire REGISTRATION_SUCCESS for post_purchase users on first SIGNED_IN
            if (event === 'SIGNED_IN' && regSource === 'post_purchase') {
              trackEvent('REGISTRATION_SUCCESS', {
                user_id: u.id,
                source: 'post_purchase',
              });
              console.log('[Auth] REGISTRATION_SUCCESS tracked for post_purchase user');
            }
          }
        } else {
          setProfile(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string, marketingConsent?: boolean) => {
    // Redirect URL for email confirmation
    const redirectUrl = `${window.location.origin}/registration-success`;

    // Pass guest_session_id so DB trigger can link prior guest consents to this user
    let guestSessionId = "";
    try {
      guestSessionId = localStorage.getItem("bp_guest_sid") || "";
    } catch {
      // ignore
    }

    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || "",
          marketing_consent: marketingConsent || false,
          registration_source: "auth",
          guest_session_id: guestSessionId,
        },
      },
    });

    return { error, data };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) {
        console.warn("Local sign out warning:", error.message);
      }
    } catch (error) {
      console.warn("Local sign out failed:", error);
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      profile, 
      loading, 
      signUp, 
      signIn, 
      signOut,
      refreshProfile 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
